import React, { useEffect, useMemo, useState } from "react";

import {
  ChevronDown,
  Clock3,
  MapPin,
  Navigation,
  Phone,
  Share2,
  Star,
} from "lucide-react";

import { useLocation, useParams } from "react-router-dom";

import Navbar from "./Navbar";
import FAQSection from "./FAQSection";
import Footer from "./Footer";
import RestaurantMenu from "./RestaurantMenu";
import { useCatalogueStatus } from "../Services/useCatalogueStatus";

import RestaurantBanner from "../Images/Restaurant_banner.png";
import { useGlobalContext } from "../Context/GlobalContext";
import RestaurantAboutGallery from "./RestaurantGallery";

const getServingLabel = (servingType) => {
  if (servingType === "V") {
    return "Pure Veg";
  }

  if (servingType === "N") {
    return "Non Veg";
  }

  if (servingType === "B") {
    return "Veg & Non Veg";
  }

  return "Restaurant";
};

const formatRestaurantTime = (time) => {
  if (!time) return "";

  const [hourString, minuteString] = String(time).split(":");

  const hour = Number(hourString);
  const minute = Number(minuteString);

  if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
    return time;
  }

  const period = hour >= 12 ? "PM" : "AM";

  const formattedHour = hour % 12 || 12;

  return `${formattedHour}:${String(minute).padStart(2, "0")} ${period}`;
};

// =========================================================
// RESTAURANT OPEN / CLOSE TIME
// =========================================================

const parseRestaurantTime = (value) => {
  const match = String(value || "")
    .trim()
    .match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)?$/i);

  if (!match) return null;

  let hour = Number(match[1]);
  const minute = Number(match[2]);
  const period = match[3]?.toUpperCase();

  if (minute > 59 || hour > 23) return null;

  if (period) {
    if (hour < 1 || hour > 12) return null;
    hour = (hour % 12) + (period === "PM" ? 12 : 0);
  }

  return hour * 60 + minute;
};

const getCurrentRestaurantTime = () => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date());

  const hour = Number(parts.find((p) => p.type === "hour")?.value || 0);
  const minute = Number(parts.find((p) => p.type === "minute")?.value || 0);

  return hour * 60 + minute;
};

export default function RestaurantHeroDetails() {
  const { userid } = useParams();

  const location = useLocation();
  const apiRestaurantDetails = location.state?.restaurantDetails || null;

  // =========================================================
  // RESTAURANT OPEN / CLOSED STATUS FROM API
  // =========================================================

  const { getCatalogueStatus } = useCatalogueStatus();

  const [catalogueStatus, setCatalogueStatus] = useState("");
  const [statusChecked, setStatusChecked] = useState(false);

  const {
    selectedRestaurant: contextRestaurant,

    selectedOrderType,
    selectedPlatform,
  } = useGlobalContext();

  const [catalogueRestaurant, setCatalogueRestaurant] = useState(null);

  const [isFavourite, setIsFavourite] = useState(false);

  const selectedRestaurant =
    location.state?.restaurant || contextRestaurant || null;

  const dynamicUserId = String(
    userid || selectedRestaurant?.userId || selectedRestaurant?.userid || "",
  ).trim();

  useEffect(() => {
    let cancelled = false;

    setCatalogueStatus("");
    setStatusChecked(false);

    if (!dynamicUserId) {
      setStatusChecked(true);
      return;
    }

    const fetchRestaurantStatus = async () => {
      const result = await getCatalogueStatus({
        userid: dynamicUserId,
      });

      if (cancelled) return;

      setCatalogueStatus(
        result?.success
          ? String(result.catalogueStatus || "")
              .trim()
              .toLowerCase()
          : "",
      );

      setStatusChecked(true);
    };

    fetchRestaurantStatus();

    return () => {
      cancelled = true;
    };
  }, [dynamicUserId, getCatalogueStatus]);

  const dynamicOrderType = String(
    location.state?.orderType || selectedOrderType || "home_delivery",
  ).trim();

  const dynamicPlatform = String(
    location.state?.platform ?? selectedPlatform ?? "",
  ).trim();

  const selectedItemName = String(
    location.state?.selectedItemName ||
      location.state?.restaurant?.selectedItemName ||
      "",
  ).trim();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "auto",
    });
  }, [dynamicUserId]);

  const restaurantName =
    catalogueRestaurant?.restaurant_name ||
    selectedRestaurant?.name ||
    selectedRestaurant?.restaurant_name ||
    "Restaurant";

  const restaurantImages = Array.isArray(selectedRestaurant?.image)
    ? selectedRestaurant.image
    : String(
        selectedRestaurant?.image ||
          selectedRestaurant?.restaurant_profile_image ||
          selectedRestaurant?.restaurant_image ||
          "",
      )
        .split(",")
        .map((url) => url.trim())
        .filter(Boolean);

  const restaurantImage =
    restaurantImages[0] ||
    catalogueRestaurant?.restaurant_image ||
    RestaurantBanner;

  const restaurantPhone =
    catalogueRestaurant?.restaurant_phone ||
    selectedRestaurant?.phone ||
    selectedRestaurant?.restaurant_phone ||
    "";

  const restaurantCity =
    catalogueRestaurant?.city || selectedRestaurant?.city || "";

  const restaurantState =
    catalogueRestaurant?.state || selectedRestaurant?.state || "";

  const restaurantStreet =
    catalogueRestaurant?.street ||
    selectedRestaurant?.address ||
    selectedRestaurant?.street ||
    "";

  const restaurantLatitude =
    catalogueRestaurant?.latitude || selectedRestaurant?.latitude;

  const restaurantLongitude =
    catalogueRestaurant?.longitude || selectedRestaurant?.longitude;

  const restaurantRating = Number(selectedRestaurant?.rating || 0);

  const restaurantOffer = String(selectedRestaurant?.offer || "").trim();

  const restaurantOpeningTime =
    apiRestaurantDetails?.openingTime ||
    catalogueRestaurant?.opening_time ||
    catalogueRestaurant?.openingTime ||
    selectedRestaurant?.openingTime ||
    selectedRestaurant?.opening_time ||
    "";

  const restaurantClosingTime =
    apiRestaurantDetails?.closingTime ||
    catalogueRestaurant?.closing_time ||
    catalogueRestaurant?.closingTime ||
    selectedRestaurant?.closingTime ||
    selectedRestaurant?.closing_time ||
    "";

  // =========================================================
  // FINAL RESTAURANT STATUS - ONLY API RESPONSE
  // =========================================================

  const isRestaurantOpen = catalogueStatus === "show";

  const isRestaurantClosed = catalogueStatus === "hide";

  // Jab tak API se "show" confirm nahi hota,
  // ordering disabled rahegi.
  const isOrderingBlocked = !isRestaurantOpen;

  const restaurantTiming =
    restaurantOpeningTime && restaurantClosingTime
      ? `${formatRestaurantTime(restaurantOpeningTime)} - ${formatRestaurantTime(
          restaurantClosingTime,
        )}`
      : restaurantOpeningTime
        ? `Opens ${formatRestaurantTime(restaurantOpeningTime)}`
        : restaurantClosingTime
          ? `Closes ${formatRestaurantTime(restaurantClosingTime)}`
          : "Not available";

  const restaurantDescription = String(
    selectedRestaurant?.description ||
      getServingLabel(selectedRestaurant?.servingType),
  ).trim();

  const restaurantDistance = useMemo(() => {
    const distance = Number(selectedRestaurant?.distance);

    if (!Number.isFinite(distance)) {
      return "Nearby";
    }

    return `${distance.toFixed(1)} KM`;
  }, [selectedRestaurant?.distance]);

  const outletName =
    [restaurantCity, restaurantState].filter(Boolean).join(", ") ||
    restaurantStreet ||
    restaurantName;

  // =========================================================
  // SCROLL TO RESTAURANT GALLERY
  // =========================================================

  const handleScrollToGallery = () => {
    // Release RestaurantMenu category scroll lock
    window.dispatchEvent(new Event("chaafo:scroll-to-top"));

    const gallerySection = document.getElementById(
      "restaurant-gallery-section",
    );

    if (!gallerySection) return;

    gallerySection.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const handleShare = async () => {
    const shareData = {
      title: restaurantName,

      text: `Check out ${restaurantName} on Chaafo`,

      url: window.location.href,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);

        return;
      }

      await navigator.clipboard.writeText(window.location.href);
    } catch (error) {
      console.error("Restaurant share error:", error);
    }
  };

  const handleOpenDirections = () => {
    const latitude = Number(restaurantLatitude);

    const longitude = Number(restaurantLongitude);

    if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
      window.open(
        `https://www.google.com/maps?q=${latitude},${longitude}`,
        "_blank",
        "noopener,noreferrer",
      );

      return;
    }

    if (restaurantStreet) {
      window.open(
        `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
          restaurantStreet,
        )}`,
        "_blank",
        "noopener,noreferrer",
      );
    }
  };

  return (
    <>
      <Navbar />

      <section className="relative w-full bg-blue-50 pb-10">
        <div className="relative h-[250px] w-full overflow-hidden bg-[#eef1f4] sm:h-[310px] lg:h-[355px]">
          <img
            src={restaurantImage}
            alt={restaurantName}
            className="h-full w-full object-cover object-center"
            onError={(event) => {
              event.currentTarget.src = RestaurantBanner;
              event.currentTarget.className =
                "h-full w-full object-cover object-center";
            }}
          />
        </div>

        <div
          onClick={handleScrollToGallery}
          onKeyDown={(event) => {
            if (
              event.target === event.currentTarget &&
              (event.key === "Enter" || event.key === " ")
            ) {
              event.preventDefault();
              handleScrollToGallery();
            }
          }}
          role="button"
          tabIndex={0}
          className="
    relative
    z-20
    mx-auto
    cursor-pointer
    -mt-[82px]
    w-[calc(100%-32px)]
    max-w-[850px]
    rounded-[12px]
    border
    border-[#dce3ea]
    bg-white
    px-5
    py-5
    shadow-[0_8px_24px_rgba(15,23,42,0.14)]

    sm:-mt-[95px]
    sm:px-6
    sm:py-6

    md:px-7
  "
        >
          {/* ===================================================== */}
          {/* RESTAURANT HEADER */}
          {/* ===================================================== */}

          <div className="relative min-w-0">
            {/* =================================================== */}
            {/* TOP RIGHT ICON BUTTONS */}
            {/* =================================================== */}

            <div
              className="
      absolute
      right-0
      top-0
      z-10

      flex
      items-center
      overflow-hidden

      rounded-[8px]
      border
      border-[#dfe5eb]
      bg-white
      shadow-sm
    "
            >
              {/* SHARE */}

              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  handleShare();
                }}
                aria-label="Share restaurant"
                className="
        flex
        h-8
        w-8
        shrink-0
        items-center
        justify-center

        border-r
        border-[#e3e8ed]

        text-[#45566c]

        transition
        hover:bg-[#f7f9fb]

        sm:h-9
        sm:w-9
      "
              >
                <Share2 size={16} strokeWidth={1.8} />
              </button>

              {/* DIRECTIONS */}

              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  handleOpenDirections();
                }}
                aria-label="Open directions"
                className="
        flex
        h-8
        w-8
        shrink-0
        items-center
        justify-center

        text-[#45566c]

        transition
        hover:bg-[#f7f9fb]

        sm:h-9
        sm:w-9
      "
              >
                <Navigation size={16} strokeWidth={1.8} />
              </button>
            </div>

            {/* =================================================== */}
            {/* NAME + RATING */}
            {/* =================================================== */}

            <div className="min-w-0 pr-[82px] sm:pr-[95px]">
              <h1
                className="
        break-words
        text-[19px]
        font-semibold
        leading-[27px]
     text-[#384152]
cursor-pointer
hover:underline
decoration-1
underline-offset-4

sm:text-[22px]
        sm:leading-[30px]

        md:text-[24px]
      "
              >
                {restaurantName}
              </h1>

              <div
                className="
    mt-3
    flex
    flex-wrap
    items-center
    gap-x-3
    gap-y-2
    text-[13px]
    text-[#91a0b5]
    sm:text-[14px]
  "
              >
                {restaurantRating > 0 && (
                  <div className="flex items-center gap-1.5 text-[#10a66a]">
                    <Star size={16} strokeWidth={0} fill="currentColor" />

                    <span className="font-semibold">
                      {restaurantRating.toFixed(1)}
                    </span>
                  </div>
                )}

                {restaurantCity && (
                  <>
                    {restaurantRating > 0 && (
                      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#c5cdd7]" />
                    )}

                    <div className="flex min-w-0 items-center gap-1.5">
                      <MapPin
                        size={14}
                        strokeWidth={1.8}
                        className="shrink-0 text-[#3e4957] font-bold"
                      />
                      <span className="truncate text-[#3e4957] font-bold">
                        {restaurantCity}
                      </span>
                    </div>
                  </>
                )}

                {restaurantTiming && (
                  <>
                    {(restaurantRating > 0 || restaurantCity) && (
                      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#c5cdd7]" />
                    )}

                    <div className="flex shrink-0 items-center gap-2 whitespace-nowrap">
                      {/* TIME */}
                      <div className="flex items-center gap-1.5">
                        <Clock3
                          size={14}
                          strokeWidth={1.8}
                          className="shrink-0 text-[#3e4957]"
                        />

                        <span className="whitespace-nowrap font-bold text-[#3e4957]">
                          {restaurantTiming}
                        </span>
                      </div>

                      {/* CLOSED STATUS */}
                      {/* RESTAURANT STATUS */}

                      {isRestaurantClosed ? (
                        <span
                          className="
      inline-flex
      shrink-0
      items-center
      gap-1.5
      rounded-full
      border
      border-[#ffd8d4]
      bg-[#fff1ef]

      px-2
      py-[3px]

      text-[8px]
      font-bold
      uppercase
      tracking-[0.03em]
      text-[#d92d20]

      min-[380px]:px-2.5
      min-[380px]:text-[9px]

      sm:text-[10px]
    "
                        >
                          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#d92d20]" />
                          Currently closed
                        </span>
                      ) : isRestaurantOpen ? (
                        <span
                          className="
      inline-flex
      shrink-0
      items-center
      gap-1.5
      rounded-full
      border
      border-[#b7efd3]
      bg-[#ecfdf3]

      px-2
      py-[3px]

      text-[8px]
      font-bold
      uppercase
      tracking-[0.03em]
      text-[#079455]

      min-[380px]:px-2.5
      min-[380px]:text-[9px]

      sm:text-[10px]
    "
                        >
                          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#12b76a]" />
                          Open
                        </span>
                      ) : (
                        <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[10px] font-bold uppercase text-amber-700">
                          <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                          {statusChecked
                            ? "Status unavailable"
                            : "Checking status..."}
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <p className="mt-3 text-[13px] leading-6 text-[#91a0b5] sm:text-[14px]">
            {restaurantDescription}

            {restaurantStreet && (
              <>
                <span className="mx-2">•</span>

                {restaurantStreet}
              </>
            )}
          </p>

          {restaurantOffer && (
            <div className="mt-3">
              <span className="inline-flex min-h-[30px] items-center gap-2 rounded-[5px] bg-[#06a96b] px-3 py-1 text-[13px] font-semibold text-white">
                <Star size={14} strokeWidth={0} fill="currentColor" />

                {restaurantOffer}
              </span>
            </div>
          )}

          <div className="mt-5 grid grid-cols-2 gap-3">
            {/* DISTANCE */}

            <div className="flex min-h-[68px] min-w-0 flex-col justify-center rounded-[8px] border border-[#e4e9ef] px-2 py-2 sm:px-4">
              <p className="text-[10px] font-medium uppercase tracking-[0.4px] text-[#9aa7b9] sm:text-[11px]">
                Distance
              </p>

              <div className="mt-2 flex min-w-0 items-center gap-1.5 text-[12px] font-semibold text-[#3e4957] sm:gap-2 sm:text-[13px]">
                <MapPin size={15} strokeWidth={1.8} className="shrink-0" />

                <span className="truncate">{restaurantDistance}</span>
              </div>
            </div>

            {/* CONTACT */}

            <div className="flex min-h-[68px] min-w-0 flex-col justify-center rounded-[8px] border border-[#e4e9ef] px-2 py-2 sm:px-4">
              <p className="text-[10px] font-medium uppercase tracking-[0.4px] text-[#9aa7b9] sm:text-[11px]">
                Contact
              </p>

              <div className="mt-2 flex min-w-0 items-center gap-1.5 text-[12px] font-semibold text-[#3e4957] sm:gap-2 sm:text-[13px]">
                <Phone size={15} strokeWidth={1.8} className="shrink-0" />

                <span className="min-w-0 truncate">
                  {restaurantPhone || "Not available"}
                </span>
              </div>
            </div>

            {/* OUTLET */}

            <button
              type="button"
              className="
    col-span-2
    flex
    min-h-[68px]
    min-w-0
    items-center
    justify-between
    rounded-[8px]
    border
    border-[#e4e9ef]
    px-2
    py-2
    text-left
    transition

    hover:border-[#9aa8b8]
    hover:bg-[#fafbfc]
  "
            >
              <div className="min-w-0">
                <p className="text-[10px] font-medium uppercase tracking-[0.4px] text-[#9aa7b9] sm:text-[11px]">
                  Outlet
                </p>

                <p className="mt-2 line-clamp-2 break-words text-[12px] font-semibold uppercase leading-5 text-[#3e4957] sm:text-[13px]">
                  {outletName}
                </p>
              </div>
            </button>
          </div>
        </div>
      </section>

      <RestaurantMenu
        userid={dynamicUserId}
        orderType={dynamicOrderType}
        platform={dynamicPlatform}
        selectedItemName={selectedItemName}
        onRestaurantLoaded={setCatalogueRestaurant}
        isRestaurantClosed={isOrderingBlocked}
      />

      <div className="h-[70px] bg-blue-50 sm:h-[100px] md:h-[55px] lg:h-[50px]" />

      <div
        id="restaurant-gallery-section"
        className="relative w-full scroll-mt-[176px] md:scroll-mt-[154px] lg:scroll-mt-[82px]"
      >
        <RestaurantAboutGallery
          restaurantDetails={{
            ...(catalogueRestaurant || {}),
            ...(selectedRestaurant || {}),
            ...(apiRestaurantDetails || {}),
          }}
        />
      </div>

      <FAQSection />

      <Footer />
    </>
  );
}
